import { ChevronUp, Keyboard, Package, ReceiptText } from 'lucide-react'
import { parseAsStringLiteral, useQueryState } from 'nuqs'
import { useRef,useState } from 'react'
import { toast } from 'sonner'
import useSWR from 'swr'

import { Money } from '@/components/ds'
import { Drawer, DrawerContent, DrawerDescription, DrawerTitle } from '@/components/ui/drawer'
import { apiFetcher } from '@/data/fetcher'
import { useIsMobile } from '@/hooks/use-mobile'
import { useCashRegisterStock } from '@/hooks/useCashRegisterStock'
import { useFeatureCheck } from '@/hooks/useFeatureCheck'
import { CashRegisterSettings } from '@invo.space/shared'
import { Item } from '@/interfaces/cash-register/Item'
import { PaymentType } from '@invo.space/shared'
import { EetSubmissionCollection } from '@invo.space/shared'
import { Settings } from '@/interfaces/Settings'
import API from '@/lib/api'
import { Printer } from '@/lib/printer/printer'
import { cn } from '@/lib/utils'
import { revalidateStockItems } from '@/lib/warehouse/stockAvailability'

import { createReceipt, SuccessCreateReceiptResponse, updateReceiptPaymentType } from './createReceipt'
import { Numpad } from './Numpad'
import { ProductCatalog } from './ProductCatalog'
import { formatQty, plural, ticketTotals } from './registerUtils'
import { PaymentTypeConfig, Ticket } from './Ticket'
import { usePersistentTicket } from './usePersistentTicket'

const MODES = ['produkty', 'vlastni'] as const
type Mode = (typeof MODES)[number]
const MODE_KEY = 'invo.cashRegister.mode'

export type CalculatorProps = {
  cashierSettings: CashRegisterSettings
  printer: Printer | null
  /** Zavolá se po uložení účtenky (např. obnovit dnešní tržbu) */
  onReceiptCreated?: () => void
}

/**
 * Varianta pokladny bez karet a dělicích čar: katalog leží přímo na plátně,
 * účtenka je jedna jemná plocha. Chování je stejné jako u původní pokladny.
 */
export function Calculator({ cashierSettings, printer, onReceiptCreated }: CalculatorProps) {
  const { items, setItems, markPaid } = usePersistentTicket()
  const isMobile = useIsMobile()
  const [ticketOpen, setTicketOpen] = useState(false)
  const itemsRef = useRef(items)
  itemsRef.current = items
  const [lastReceiptLink, setLastReceiptLink] = useState<string | null>(null)
  const [lastReceiptId, setLastReceiptId] = useState<string | null>(null)
  // bez parametru v URL platí naposledy použitý pohled; přepnutí je krok v historii (Zpět vrací)
  const [defaultMode] = useState<Mode>(() => (localStorage.getItem(MODE_KEY) === 'vlastni' ? 'vlastni' : 'produkty'))
  const [modeState, setModeState] = useQueryState(
    'pohled',
    parseAsStringLiteral(MODES).withDefault(defaultMode).withOptions({ history: 'push' }),
  )

  const { isEnabled: catalogEnabled } = useFeatureCheck('catalog')
  const { isEnabled: warehouseEnabled } = useFeatureCheck('warehouse')
  const { data: settings } = useSWR<Settings>('/api/v2/settings', apiFetcher)
  const isVatPayer = settings?.vatPayer === 'vatPayer'
  const warehouseId = cashierSettings.warehouseId ?? null
  const { getAvailableToAdd, getMaxQuantityForLine } = useCashRegisterStock(warehouseEnabled === true, items, warehouseId)

  const mode: Mode = catalogEnabled ? modeState : 'vlastni'
  const setMode = (next: Mode) => {
    if (next === mode) return
    setModeState(next)
    localStorage.setItem(MODE_KEY, next)
  }

  const hasIban = Boolean(settings?.participant?.iban)
  const enabledPaymentTypes = cashierSettings.paymentTypes ?? [PaymentType.Card, PaymentType.Cash, PaymentType.Qr]

  const paymentTypes: PaymentTypeConfig[] = [
    { type: PaymentType.Cash, enabled: enabledPaymentTypes.includes(PaymentType.Cash), allowed: true },
    { type: PaymentType.Card, enabled: enabledPaymentTypes.includes(PaymentType.Card), allowed: true },
    {
      type: PaymentType.Qr,
      enabled: enabledPaymentTypes.includes(PaymentType.Qr),
      allowed: hasIban,
      disallowedTooltip: 'Nejdříve musíte ve svých fakturačních údajích nastavit IBAN',
    },
  ]

  const handleComplete = (paymentType: PaymentType): Promise<SuccessCreateReceiptResponse> => {
    if (items.length === 0) {
      return Promise.reject()
    }

    return createReceipt(paymentType, items, warehouseId).then(async (result: SuccessCreateReceiptResponse) => {
      revalidateStockItems()
      markPaid()
      setLastReceiptLink(result.permalink)
      setLastReceiptId(result.id)
      onReceiptCreated?.()

      try {
        const eetResponse = await API.get<EetSubmissionCollection>(`/api/v2/eet-submissions?receiptId=${result.id}`)
        const submission = eetResponse.data.data[0]
        if (submission?.pok) {
          toast.success(`EET 2.0 POK: ${submission.pok}`)
        } else if (submission?.status === 'offline') {
          toast.warning('EET 2.0: odeslání se nepodařilo, bude opakováno')
        } else if (submission?.status === 'rejected') {
          toast.error(`EET 2.0 chyba: ${submission.errorMessage ?? submission.errorCode}`)
        }
      } catch {
        // EET 2.0 není nastavené nebo se na účtenku nevztahuje
      }

      return result
    })
  }

  const handleChangePayment = (paymentType: PaymentType): Promise<SuccessCreateReceiptResponse> => {
    if (lastReceiptId === null) {
      return Promise.reject()
    }

    return updateReceiptPaymentType(lastReceiptId, paymentType).then((result) => {
      setLastReceiptLink(result.permalink)
      onReceiptCreated?.()
      return result
    })
  }

  const handlePrint = () => {
    if (lastReceiptLink === null) {
      toast.error('Není co tisknout')
      return
    }

    const printCallback = (p: Printer) => {
      fetch(lastReceiptLink + '.txt')
        .then((res) => res.text())
        .then((receiptText) => p.print(receiptText))
        .catch(() => toast.error('Účtenku se nepodařilo vytisknout'))
    }

    if (!printer?.isReady()) {
      printer?.requestDevice().then(() => printCallback(printer))
      return
    }

    printCallback(printer)
  }

  const updateQuantity = (index: number, newQuantity: number) => {
    const item = items[index]
    let cappedQuantity = newQuantity
    if (item.skuId) {
      cappedQuantity = Math.min(newQuantity, getMaxQuantityForLine(item.skuId, index))
    }
    const updatedItems = [...items]
    updatedItems[index] = updatedItems[index].setAmount(cappedQuantity)
    setItems(updatedItems)
  }

  const tabClass = (active: boolean) =>
    cn(
      'inline-flex h-9 items-center gap-2 rounded-full px-3 text-sm font-medium transition-colors select-none md:h-11 md:px-4',
      'focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-none',
      active ? 'bg-muted text-foreground' : 'text-muted-foreground hover:text-foreground',
    )

  const ticketEl = (
    <Ticket
      items={items}
      isVatPayer={isVatPayer}
      paymentTypes={paymentTypes}
      onRemove={(index) => setItems(items.filter((_, i) => i !== index))}
      onQuantityChange={updateQuantity}
      onSaleChange={(index, sale) => {
        const next = [...items]
        next[index] = next[index].setSale(sale.value === 0 ? undefined : sale)
        setItems(next)
      }}
      onDescriptionChange={(index, description) => {
        const next = [...items]
        next[index] = next[index].setDescription(description)
        setItems(next)
      }}
      onClear={() => setItems([])}
      onDiscard={() => {
        const previous = items
        setItems([])
        toast('Účtenka vymazána', {
          id: 'ticket-discard',
          duration: 7000,
          // když mezitím přibyly nové položky, vrácené se přidají před ně
          action: { label: 'Vrátit', onClick: () => setItems([...previous, ...itemsRef.current]) },
        })
      }}
      onComplete={handleComplete}
      onChangePayment={handleChangePayment}
      onPrint={handlePrint}
    />
  )

  const pieces = items.reduce((sum, i) => sum + i.amount, 0)
  const { total } = ticketTotals(items)

  return (
    // dvousloupcové rozložení podle šířky obsahu (sidebar ji zmenšuje), ne podle šířky okna
    <div className="@container lg:min-h-0 lg:flex-1 lg:overflow-y-auto">
    <div className="grid gap-6 @[52rem]:h-full @[52rem]:grid-cols-[minmax(0,1fr)_360px] @[64rem]:grid-cols-[minmax(0,1fr)_400px] @[64rem]:gap-8 @[76rem]:grid-cols-[minmax(0,1fr)_460px]">
      <section aria-label="Přidat na účtenku" className="flex min-h-[420px] min-w-0 flex-col gap-3 md:gap-4 @[52rem]:min-h-0">
        {catalogEnabled && (
          <div role="tablist" aria-label="Způsob přidání" className="flex shrink-0 items-center gap-1">
            <button type="button" role="tab" aria-selected={mode === 'produkty'} className={tabClass(mode === 'produkty')} onClick={() => setMode('produkty')}>
              <Package className="size-4" aria-hidden />
              Produkty
            </button>
            <button type="button" role="tab" aria-selected={mode === 'vlastni'} className={tabClass(mode === 'vlastni')} onClick={() => setMode('vlastni')}>
              <Keyboard className="size-4" aria-hidden />
              Vlastní položka
            </button>
          </div>
        )}

        {mode === 'produkty' ? (
          <ProductCatalog
            items={items}
            onItemsChange={setItems}
            getAvailableToAdd={getAvailableToAdd}
            getMaxQuantityForLine={getMaxQuantityForLine}
          />
        ) : (
          <div className="min-h-0 flex-1 overflow-y-auto py-2 @[52rem]:grid @[52rem]:place-items-center">
            <Numpad
              cashierSettings={cashierSettings}
              isVatPayer={isVatPayer}
              active={mode === 'vlastni'}
              onPlus={(amount, price, tax) =>
                setItems([
                  ...items,
                  cashierSettings.amountThenPrice || amount === 1 ? new Item(amount, price, tax) : new Item(price, amount, tax),
                ])
              }
            />
          </div>
        )}
      </section>

      {isMobile ? null : ticketEl}
    </div>

    {isMobile && (
      <>
        {/* Na telefonu je účtenka v panelu zespodu, dole zůstává jen lišta s počtem a součtem */}
        <div className="sticky bottom-0 z-20 -mx-6 mt-3 border-t bg-background/95 px-4 pt-2 pb-[max(0.75rem,env(safe-area-inset-bottom))] backdrop-blur supports-[backdrop-filter]:bg-background/85">
          <button
            type="button"
            onClick={() => setTicketOpen(true)}
            className="flex h-14 w-full items-center gap-3 rounded-xl bg-primary px-4 text-primary-foreground active:bg-primary/90"
          >
            <ReceiptText className="size-5 shrink-0" aria-hidden />
            <span className="flex min-w-0 flex-1 flex-col items-start leading-tight">
              <span className="text-[15px] font-medium">Účtenka</span>
              <span className="text-xs text-primary-foreground/80">
                {items.length === 0 ? 'Zatím prázdná' : `${formatQty(pieces)} ${plural(pieces, 'kus', 'kusy', 'kusů')}`}
              </span>
            </span>
            <Money amount={total} minor={false} size="lg" animate className="text-primary-foreground [&_*]:text-primary-foreground" />
            <ChevronUp className="size-5 shrink-0 opacity-80" aria-hidden />
          </button>
        </div>

        {/* tahací panel (vaul): stáhnutím dolů, klepnutím mimo nebo Esc se zavře */}
        <Drawer open={ticketOpen} onOpenChange={setTicketOpen}>
          <DrawerContent
            className="h-[88svh] gap-0 rounded-t-2xl border-0 data-[vaul-drawer-direction=bottom]:max-h-[88svh]"
            onPointerDownOutside={(e) => {
              if (e.target instanceof Element && e.target.closest('[data-sonner-toaster]')) e.preventDefault()
            }}
          >
            <DrawerTitle className="sr-only">Účtenka</DrawerTitle>
            <DrawerDescription className="sr-only">Položky na účtence a platba. Panel zavřete stažením dolů.</DrawerDescription>
            <div className="flex min-h-0 flex-1 flex-col px-2 pt-2 pb-[env(safe-area-inset-bottom)] [&>section]:min-h-0 [&>section]:flex-1">
              {ticketEl}
            </div>
          </DrawerContent>
        </Drawer>
      </>
    )}
    </div>
  )
}
